import React, { useState, useEffect } from 'react';
import { X, Award, Save, Edit3 } from 'lucide-react';
import api from '../api';
import { toast } from 'react-toastify';

const TableauHonneurModal = ({ isOpen, onClose, bulletinId, onUpdate }) => {
  const [loading, setLoading] = useState(false);
  const [tableauHonneur, setTableauHonneur] = useState(null);
  const [formData, setFormData] = useState({
    decision_conseil: 'AUCUN',
    appreciation_conseil: ''
  });

  const decisions = [
    { value: 'AUCUN', label: 'Aucun', color: 'text-gray-600' },
    { value: 'FELICITATION', label: 'Félicitation', color: 'text-green-600' },
    { value: 'ENCOURAGEMENT', label: 'Encouragement', color: 'text-blue-600' },
    { value: 'TABLEAU_D_HONNEUR', label: 'Tableau d\'honneur', color: 'text-purple-600' },
    { value: 'AVERTISSEMENT', label: 'Avertissement', color: 'text-yellow-600' },
    { value: 'BLAME', label: 'Blâme', color: 'text-red-600' }
  ];

  useEffect(() => {
    if (isOpen && bulletinId) {
      fetchTableauHonneur();
    }
  }, [isOpen, bulletinId]);

  const fetchTableauHonneur = async () => {
    try {
      const response = await api.get(`/note/tableau-honneur/${bulletinId}/`);
      if (response.data.success && response.data.data) {
        setTableauHonneur(response.data.data);
        setFormData({
          decision_conseil: response.data.data.decision_conseil || 'AUCUN',
          appreciation_conseil: response.data.data.appreciation_conseil || ''
        });
      } else {
        setTableauHonneur(null);
        setFormData({
          decision_conseil: 'AUCUN',
          appreciation_conseil: ''
        });
      }
    } catch (error) {
      console.error('Erreur lors de la récupération du tableau d\'honneur:', error);
      setTableauHonneur(null);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await api.post(`/note/tableau-honneur/${bulletinId}/`, formData);
      
      if (response.data.success) {
        toast.success('Tableau d\'honneur mis à jour avec succès');
        setTableauHonneur(response.data.data);
        if (onUpdate) {
          onUpdate(response.data.data);
        }
        onClose();
      } else {
        toast.error('Erreur lors de la mise à jour');
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!tableauHonneur) return;
    
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce tableau d\'honneur ?')) {
      try {
        await api.delete(`/note/tableau-honneur/${bulletinId}/`);
        toast.success('Tableau d\'honneur supprimé avec succès');
        setTableauHonneur(null);
        setFormData({
          decision_conseil: 'AUCUN',
          appreciation_conseil: ''
        });
        if (onUpdate) {
          onUpdate(null);
        }
      } catch (error) {
        console.error('Erreur lors de la suppression:', error);
        toast.error('Erreur lors de la suppression');
      }
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <Award className="h-6 w-6 text-[#202654]" />
            <h2 className="text-xl font-bold text-gray-900">
              {tableauHonneur ? 'Modifier le' : 'Créer le'} Tableau d'Honneur
            </h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Décision du conseil */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Décision du conseil des professeurs *
            </label>
            <div className="grid grid-cols-2 gap-3">
              {decisions.map((decision) => (
                <label
                  key={decision.value}
                  className={`flex items-center p-3 border rounded-lg cursor-pointer transition-colors ${
                    formData.decision_conseil === decision.value
                      ? 'border-[#202654] bg-[#202654]/5'
                      : 'border-gray-300 hover:border-gray-400'
                  }`}
                >
                  <input
                    type="radio"
                    name="decision_conseil"
                    value={decision.value}
                    checked={formData.decision_conseil === decision.value}
                    onChange={(e) => setFormData({
                      ...formData,
                      decision_conseil: e.target.value
                    })}
                    className="mr-3 text-[#202654] focus:ring-[#202654]"
                  />
                  <span className={`font-medium ${decision.color}`}>
                    {decision.label}
                  </span>
                </label>
              ))}
            </div>
          </div>

          {/* Appréciation */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Appréciation détaillée du conseil
            </label>
            <textarea
              value={formData.appreciation_conseil}
              onChange={(e) => setFormData({
                ...formData,
                appreciation_conseil: e.target.value
              })}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#202654] focus:border-transparent"
              placeholder="Appréciation détaillée du conseil des professeurs..."
            />
          </div>

          {/* Informations actuelles */}
          {tableauHonneur && (
            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Informations actuelles
              </h4>
              <div className="text-sm text-gray-600 space-y-1">
                <p>Décision: <span className="font-medium">{tableauHonneur.decision_conseil_display}</span></p>
                <p>Date: <span className="font-medium">
                  {new Date(tableauHonneur.date_decision).toLocaleDateString('fr-FR')}
                </span></p>
                {tableauHonneur.decide_par_nom && (
                  <p>Décidé par: <span className="font-medium">{tableauHonneur.decide_par_nom}</span></p>
                )}
              </div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center justify-between pt-4 border-t border-gray-200">
            <div className="flex space-x-3">
              {tableauHonneur && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="px-4 py-2 text-sm font-medium text-red-600 border border-red-300 rounded-md hover:bg-red-50 focus:outline-none focus:ring-2 focus:ring-red-500"
                >
                  Supprimer
                </button>
              )}
            </div>
            
            <div className="flex space-x-3">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#202654]"
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-[#202654] border border-transparent rounded-md hover:bg-[#202654]/90 focus:outline-none focus:ring-2 focus:ring-[#202654] disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                    <span>Sauvegarde...</span>
                  </>
                ) : (
                  <>
                    {tableauHonneur ? <Edit3 className="h-4 w-4" /> : <Save className="h-4 w-4" />}
                    <span>{tableauHonneur ? 'Modifier' : 'Créer'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TableauHonneurModal;
